import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Grid,
  MenuItem,
  Pagination,
  Snackbar,
  Stack,
  Switch,
  Tab,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import AppointmentService from "../services/AppointmentService";
import appConfig from "../shared/js/app-config";
import { bookingContactSearchHaystack, formatBookingContact } from "../shared/utils/bookingContactDisplay";

const dayLabels = [
  ["mon", "Mon"],
  ["tue", "Tue"],
  ["wed", "Wed"],
  ["thu", "Thu"],
  ["fri", "Fri"],
  ["sat", "Sat"],
  ["sun", "Sun"],
];

const defaultHours = () =>
  dayLabels.reduce((acc, [key]) => {
    acc[key] = key === "sat" || key === "sun" ? [] : [{ from: "09:00", to: "17:00" }];
    return acc;
  }, {});

const emptyType = {
  id: null,
  code: "",
  name: "",
  duration_minutes: 30,
  buffer_before_minutes: 0,
  buffer_after_minutes: 0,
  price_minor: "",
  deposit_minor: "",
  currency: "INR",
  is_active: true,
};

const emptyResource = {
  id: null,
  code: "",
  name: "",
  timezone: "Asia/Kolkata",
  appointment_type_ids: [],
  staff_account_ids: [],
  calendar_visibility: "team",
  booking_manage_permission: "admin_or_assigned",
  external_calendar_provider: "none",
  external_calendar_id: "",
  external_calendar_sync_direction: "two_way",
  external_calendar_busy_only: true,
  is_active: true,
};

const emptyException = { date: "", closed: true, hours: [{ from: "09:00", to: "17:00" }] };
const emptyRecurringRule = {
  name: "",
  enabled: true,
  days: ["mon", "tue", "wed", "thu", "fri"],
  start_date: "",
  end_date: "",
  closed: false,
  hours: [{ from: "09:00", to: "17:00" }],
};
const emptyBlackoutTemplate = { name: "", enabled: true, start_date: "", end_date: "" };
const APPOINTMENT_PAGE_SIZE = 12;
/** Fixed month cell height so one busy day does not stretch the whole row. */
const MONTH_CELL_HEIGHT_PX = 124;
const MONTH_VISIBLE_BOOKINGS = 2;
const WEEK_CELL_MAX_HEIGHT_PX = 220;

function dateInputValue(value) {
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  const local = new Date(d.getTime() - d.getTimezoneOffset() * 60000);
  return local.toISOString().slice(0, 10);
}

function addDays(value, days) {
  const d = value instanceof Date ? new Date(value) : new Date(`${value}T00:00:00`);
  d.setDate(d.getDate() + days);
  return d;
}

function startOfWeek(value) {
  const d = value instanceof Date ? new Date(value) : new Date(`${value}T00:00:00`);
  const day = d.getDay();
  const diff = day === 0 ? -6 : 1 - day;
  return addDays(d, diff);
}

function startOfMonth(value) {
  const d = value instanceof Date ? new Date(value) : new Date(`${value}T00:00:00`);
  return new Date(d.getFullYear(), d.getMonth(), 1);
}

function dayStart(value) {
  if (!value) return null;
  const d = new Date(`${value}T00:00:00`);
  return Number.isNaN(d.getTime()) ? null : d;
}

function dayEnd(value) {
  if (!value) return null;
  const d = new Date(`${value}T23:59:59`);
  return Number.isNaN(d.getTime()) ? null : d;
}

function parseJson(raw, fallback = {}) {
  if (!raw) return fallback;
  if (typeof raw === "object") return raw;
  try {
    return JSON.parse(raw);
  } catch {
    return fallback;
  }
}

function asArray(value) {
  return Array.isArray(value) ? value : [];
}

function formatMoney(currency, minor) {
  const amount = Number(minor || 0) / 100;
  return `${currency || "INR"} ${amount.toFixed(2)}`;
}

function toMinor(value) {
  if (value === "" || value == null) return null;
  const n = Number(value);
  return Number.isFinite(n) ? Math.round(n * 100) : null;
}

function majorFromMinor(minor) {
  if (minor === "" || minor == null) return "";
  const n = Number(minor);
  return Number.isFinite(n) ? n / 100 : "";
}

function normalizeIdArray(value) {
  const arr = Array.isArray(value) ? value : String(value || "").split(",");
  return arr.map((v) => Number(v)).filter((v) => Number.isFinite(v));
}

function normalizeStringArray(value) {
  const arr = Array.isArray(value) ? value : String(value || "").split(",");
  return arr.map((v) => String(v).trim()).filter(Boolean);
}

function friendlyKey(value, fallback = "item") {
  const key = String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
  return key || fallback;
}

function getResourceStaffAccess(resourceOrForm) {
  const meta = parseJson(resourceOrForm?.metadata_json, {});
  return {
    staff_account_ids: normalizeStringArray(resourceOrForm?.staff_account_ids || meta.staff_access?.staff_account_ids),
    calendar_visibility: resourceOrForm?.calendar_visibility || meta.staff_access?.calendar_visibility || "team",
    booking_manage_permission:
      resourceOrForm?.booking_manage_permission || meta.staff_access?.booking_manage_permission || "admin_or_assigned",
  };
}

function getResourceExternalCalendar(resourceOrForm) {
  const meta = parseJson(resourceOrForm?.metadata_json, {});
  return {
    provider: resourceOrForm?.external_calendar_provider || meta.external_calendar?.provider || "none",
    calendar_id: resourceOrForm?.external_calendar_id || meta.external_calendar?.calendar_id || "",
    sync_direction:
      resourceOrForm?.external_calendar_sync_direction || meta.external_calendar?.sync_direction || "two_way",
    busy_only: resourceOrForm?.external_calendar_busy_only ?? meta.external_calendar?.busy_only ?? true,
  };
}

function useNotice() {
  const [notice, setNotice] = useState({ open: false, message: "", severity: "success" });
  const show = (message, severity = "success") => setNotice({ open: true, message, severity });
  const node = (
    <Snackbar
      open={notice.open}
      autoHideDuration={3500}
      onClose={() => setNotice((n) => ({ ...n, open: false }))}
    >
      <Alert severity={notice.severity} onClose={() => setNotice((n) => ({ ...n, open: false }))}>
        {notice.message}
      </Alert>
    </Snackbar>
  );
  return { show, node };
}

function SectionCard({ title, subtitle, children, action, sx }) {
  return (
    <Card
      variant="outlined"
      sx={{
        width: "100%",
        borderRadius: 3,
        borderColor: "rgba(15,23,42,0.10)",
        boxShadow: "0 10px 28px rgba(15,23,42,0.04)",
        ...sx,
      }}
    >
      <CardContent sx={{ p: 2.25 }}>
        <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" gap={2} mb={2}>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 750 }}>{title}</Typography>
            {subtitle && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                {subtitle}
              </Typography>
            )}
          </Box>
          {action}
        </Stack>
        {children}
      </CardContent>
    </Card>
  );
}

function AppointmentOperationsView() {
  const serviceId = appConfig.getServiceId();
  const [tab, setTab] = useState("types");
  const [loading, setLoading] = useState(false);
  const [types, setTypes] = useState([]);
  const [resources, setResources] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [staff, setStaff] = useState([]);
  const [registry, setRegistry] = useState(null);
  const [typeForm, setTypeForm] = useState(emptyType);
  const [resourceForm, setResourceForm] = useState(emptyResource);
  const [typeDialogOpen, setTypeDialogOpen] = useState(false);
  const [resourceDialogOpen, setResourceDialogOpen] = useState(false);
  const [selectedResourceId, setSelectedResourceId] = useState("");
  const [weeklyHours, setWeeklyHours] = useState(defaultHours());
  const [exceptions, setExceptions] = useState([]);
  const [recurringRules, setRecurringRules] = useState([]);
  const [blackoutTemplates, setBlackoutTemplates] = useState([]);
  const [holidayRegion, setHolidayRegion] = useState("");
  const [rescheduleForm, setRescheduleForm] = useState({ id: null, starts_at_utc: "", resource_id: "" });
  const [calendarDate, setCalendarDate] = useState(() => dateInputValue(new Date()));
  const [calendarView, setCalendarView] = useState("week");
  const [calendarStaffFilter, setCalendarStaffFilter] = useState("");
  const [typeFilters, setTypeFilters] = useState({ search: "", status: "" });
  const [resourceFilters, setResourceFilters] = useState({
    search: "",
    status: "",
    service_id: "",
    staff_id: "",
  });
  const [bookingListFilters, setBookingListFilters] = useState({
    search: "",
    status: "",
    type_id: "",
    resource_id: "",
    date_from: "",
    date_to: "",
  });
  const [typePage, setTypePage] = useState(1);
  const [resourcePage, setResourcePage] = useState(1);
  const [bookingPage, setBookingPage] = useState(1);
  const [chatbotPreview, setChatbotPreview] = useState({
    appointment_type_id: "",
    resource_id: "",
  });
  const { show, node: noticeNode } = useNotice();

  const selectedResource = useMemo(
    () => resources.find((r) => String(r.id) === String(selectedResourceId)),
    [resources, selectedResourceId]
  );

  const loadAll = async () => {
    if (!serviceId) return;
    setLoading(true);
    try {
      const [nextTypes, nextResources, nextBookings, nextStaff, nextRegistry] = await Promise.all([
        AppointmentService.listTypes(serviceId),
        AppointmentService.listResources(serviceId),
        AppointmentService.listBookings(serviceId),
        AppointmentService.listStaff(serviceId).catch(() => []),
        AppointmentService.actionRegistry().catch(() => null),
      ]);
      setTypes(asArray(nextTypes));
      setResources(asArray(nextResources));
      setBookings(asArray(nextBookings));
      setStaff(asArray(nextStaff));
      setRegistry(nextRegistry);
      if (!selectedResourceId && asArray(nextResources)[0]?.id) {
        setSelectedResourceId(String(nextResources[0].id));
      }
    } catch (e) {
      show("Could not load appointment data", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serviceId]);

  useEffect(() => {
    if (!selectedResource) return;
    const meta = parseJson(selectedResource.metadata_json, {});
    const availability = meta.availability || {};
    const staffAccess = getResourceStaffAccess(selectedResource);
    const externalCalendar = getResourceExternalCalendar(selectedResource);
    setWeeklyHours({ ...defaultHours(), ...(availability.weekly_hours || {}) });
    setExceptions(asArray(availability.exceptions));
    setRecurringRules(asArray(availability.recurring_rules));
    setBlackoutTemplates(asArray(availability.blackout_templates));
    setHolidayRegion(availability.holiday_region || "");
    setResourceForm({
      id: selectedResource.id,
      code: selectedResource.code || "",
      name: selectedResource.name || "",
      timezone: selectedResource.timezone || "Asia/Kolkata",
      appointment_type_ids: asArray(selectedResource.appointment_type_ids).map((id) => Number(id)),
      staff_account_ids: staffAccess.staff_account_ids,
      calendar_visibility: staffAccess.calendar_visibility,
      booking_manage_permission: staffAccess.booking_manage_permission,
      external_calendar_provider: externalCalendar.provider,
      external_calendar_id: externalCalendar.calendar_id,
      external_calendar_sync_direction: externalCalendar.sync_direction,
      external_calendar_busy_only: externalCalendar.busy_only,
      is_active: selectedResource.is_active !== false && selectedResource.is_active !== 0,
    });
  }, [selectedResource]);

  useEffect(() => {
    setTypePage(1);
  }, [typeFilters]);

  useEffect(() => {
    setResourcePage(1);
  }, [resourceFilters]);

  useEffect(() => {
    setBookingPage(1);
  }, [bookingListFilters]);

  const buildResourceMetadata = (baseResource, form = resourceForm) => {
    const existingMeta = parseJson(baseResource?.metadata_json, {});
    return {
      ...existingMeta,
      staff_access: {
        staff_account_ids: normalizeStringArray(form.staff_account_ids),
        calendar_visibility: form.calendar_visibility || "team",
        booking_manage_permission: form.booking_manage_permission || "admin_or_assigned",
      },
      external_calendar: {
        provider: form.external_calendar_provider || "none",
        calendar_id: form.external_calendar_provider === "none" ? "" : String(form.external_calendar_id || "").trim(),
        sync_direction: form.external_calendar_sync_direction || "two_way",
        busy_only: form.external_calendar_busy_only !== false,
      },
    };
  };

  const staffLabel = (accountId) => {
    const row = staff.find((s) => String(s.id || s.account_id) === String(accountId));
    if (!row) return accountId;
    const name = [row.first_name, row.last_name].filter(Boolean).join(" ").trim();
    return name || row.email || accountId;
  };

  const resourceStaffIds = (resourceId) => {
    const resource = resources.find((r) => Number(r.id) === Number(resourceId));
    return getResourceStaffAccess(resource).staff_account_ids;
  };

  const currentAccountId = appConfig.getAccountId();
  const currentStaffRow = staff.find((s) => String(s.id || s.account_id) === String(currentAccountId));
  const isOwnerLike = Boolean(currentAccountId) && !currentStaffRow;
  const canManageBooking = (booking) => {
    if (isOwnerLike || currentStaffRow?.role === "admin") return true;
    const resource = resources.find((r) => Number(r.id) === Number(booking.resource_id));
    const access = getResourceStaffAccess(resource);
    if (access.booking_manage_permission === "assigned_staff_only") {
      return access.staff_account_ids.some((id) => String(id) === String(currentAccountId));
    }
    return access.booking_manage_permission !== "admin_only";
  };

  const calendarBookings = calendarStaffFilter
    ? bookings.filter((booking) =>
      resourceStaffIds(booking.resource_id).some((id) => String(id) === String(calendarStaffFilter)))
    : bookings;

  const filteredTypes = useMemo(() => {
    const search = typeFilters.search.trim().toLowerCase();
    return types.filter((type) => {
      const archived = type.is_active === false || type.is_active === 0;
      if (typeFilters.status === "active" && archived) return false;
      if (typeFilters.status === "archived" && !archived) return false;
      if (search) {
        const haystack = [
          type.name,
          type.code,
          type.currency,
          `${type.duration_minutes} min`,
        ].filter(Boolean).join(" ").toLowerCase();
        if (!haystack.includes(search)) return false;
      }
      return true;
    });
  }, [types, typeFilters]);

  const typePageCount = Math.max(1, Math.ceil(filteredTypes.length / APPOINTMENT_PAGE_SIZE));
  const currentTypePage = Math.min(typePage, typePageCount);
  const visibleTypes = filteredTypes.slice(
    (currentTypePage - 1) * APPOINTMENT_PAGE_SIZE,
    currentTypePage * APPOINTMENT_PAGE_SIZE
  );

  const filteredResources = useMemo(() => {
    const search = resourceFilters.search.trim().toLowerCase();
    return resources.filter((resource) => {
      const archived = resource.is_active === false || resource.is_active === 0;
      const staffIds = getResourceStaffAccess(resource).staff_account_ids;
      const serviceIds = asArray(resource.appointment_type_ids).map(String);
      if (resourceFilters.status === "active" && archived) return false;
      if (resourceFilters.status === "archived" && !archived) return false;
      if (resourceFilters.service_id && serviceIds.length && !serviceIds.includes(String(resourceFilters.service_id))) return false;
      if (resourceFilters.staff_id && !staffIds.some((id) => String(id) === String(resourceFilters.staff_id))) return false;
      if (search) {
        const serviceNames = serviceIds
          .map((id) => types.find((type) => String(type.id) === String(id))?.name || id)
          .join(" ");
        const haystack = [
          resource.name,
          resource.code,
          resource.timezone,
          serviceNames,
          staffIds.map(staffLabel).join(" "),
          getResourceExternalCalendar(resource).provider,
        ].filter(Boolean).join(" ").toLowerCase();
        if (!haystack.includes(search)) return false;
      }
      return true;
    });
  }, [resources, resourceFilters, types, staff]);

  const resourcePageCount = Math.max(1, Math.ceil(filteredResources.length / APPOINTMENT_PAGE_SIZE));
  const currentResourcePage = Math.min(resourcePage, resourcePageCount);
  const visibleResources = filteredResources.slice(
    (currentResourcePage - 1) * APPOINTMENT_PAGE_SIZE,
    currentResourcePage * APPOINTMENT_PAGE_SIZE
  );

  const filteredBookingRows = useMemo(() => {
    const search = bookingListFilters.search.trim().toLowerCase();
    const fromDate = dayStart(bookingListFilters.date_from);
    const toDate = dayEnd(bookingListFilters.date_to);
    return bookings.filter((booking) => {
      if (bookingListFilters.status && booking.status !== bookingListFilters.status) return false;
      if (bookingListFilters.type_id && String(booking.appointment_type_id) !== String(bookingListFilters.type_id)) return false;
      if (bookingListFilters.resource_id && String(booking.resource_id || "") !== String(bookingListFilters.resource_id)) return false;
      const start = booking.starts_at_utc ? new Date(booking.starts_at_utc) : null;
      if ((fromDate || toDate) && !start) return false;
      if (fromDate && start && start < fromDate) return false;
      if (toDate && start && start > toDate) return false;
      if (search) {
        const type = types.find((item) => Number(item.id) === Number(booking.appointment_type_id));
        const resource = resources.find((item) => Number(item.id) === Number(booking.resource_id));
        const haystack = [
          booking.id,
          bookingContactSearchHaystack(booking),
          booking.status,
          type?.name,
          resource?.name,
        ].filter(Boolean).join(" ").toLowerCase();
        if (!haystack.includes(search)) return false;
      }
      return true;
    });
  }, [bookings, bookingListFilters, types, resources]);

  const bookingPageCount = Math.max(1, Math.ceil(filteredBookingRows.length / APPOINTMENT_PAGE_SIZE));
  const currentBookingPage = Math.min(bookingPage, bookingPageCount);
  const visibleBookingRows = filteredBookingRows.slice(
    (currentBookingPage - 1) * APPOINTMENT_PAGE_SIZE,
    currentBookingPage * APPOINTMENT_PAGE_SIZE
  );

  const saveType = async () => {
    const name = typeForm.name.trim();
    const payload = {
      code: typeForm.code.trim() || friendlyKey(name, "service").toUpperCase(),
      name,
      duration_minutes: Number(typeForm.duration_minutes || 30),
      buffer_before_minutes: Number(typeForm.buffer_before_minutes || 0),
      buffer_after_minutes: Number(typeForm.buffer_after_minutes || 0),
      price_minor: toMinor(typeForm.price_minor),
      deposit_minor: toMinor(typeForm.deposit_minor),
      currency: typeForm.currency || "INR",
      is_active: typeForm.is_active !== false,
    };
    if (!payload.name) {
      show("Service name is required", "error");
      return;
    }
    try {
      if (typeForm.id) {
        await AppointmentService.updateType(typeForm.id, payload, serviceId);
        show("Appointment service updated");
      } else {
        await AppointmentService.createType(payload, serviceId);
        show("Appointment service created");
      }
      setTypeForm(emptyType);
      setTypeDialogOpen(false);
      loadAll();
    } catch (e) {
      show("Could not save appointment service", "error");
    }
  };

  const saveResource = async () => {
    const name = resourceForm.name.trim();
    const payload = {
      code: resourceForm.code.trim() || friendlyKey(name, "resource").toUpperCase(),
      name,
      timezone: resourceForm.timezone || "Asia/Kolkata",
      is_active: resourceForm.is_active !== false,
      appointment_type_ids: resourceForm.appointment_type_ids || [],
      metadata_json: buildResourceMetadata(selectedResource),
    };
    if (!payload.name) {
      show("Resource name is required", "error");
      return;
    }
    try {
      let saved;
      if (resourceForm.id) {
        saved = await AppointmentService.updateResource(resourceForm.id, payload, serviceId);
        show("Resource updated");
      } else {
        saved = await AppointmentService.createResource(payload, serviceId);
        show("Resource created");
      }
      setResourceForm(emptyResource);
      setResourceDialogOpen(false);
      if (saved?.id) setSelectedResourceId(String(saved.id));
      loadAll();
    } catch (e) {
      show("Could not save resource", "error");
    }
  };

  const saveAvailability = async () => {
    if (!selectedResource) {
      show("Select a resource first", "error");
      return;
    }
    const existingMeta = buildResourceMetadata(selectedResource);
    const metadata_json = {
      ...existingMeta,
      availability: {
        timezone: resourceForm.timezone || selectedResource.timezone || "Asia/Kolkata",
        weekly_hours: weeklyHours,
        exceptions,
        recurring_rules: recurringRules,
        blackout_templates: blackoutTemplates,
        holiday_region: holidayRegion || null,
        holiday_policy: holidayRegion ? "closed" : "ignore",
      },
    };
    try {
      await AppointmentService.updateResource(
        selectedResource.id,
        {
          code: resourceForm.code,
          name: resourceForm.name,
          timezone: resourceForm.timezone,
          is_active: resourceForm.is_active,
          appointment_type_ids: resourceForm.appointment_type_ids || [],
          metadata_json,
        },
        serviceId
      );
      show("Availability saved");
      loadAll();
    } catch (e) {
      show("Could not save availability", "error");
    }
  };

  const cancelBooking = async (booking) => {
    try {
      await AppointmentService.cancelBooking(booking.id, { reason: "merchant_cancelled", by_merchant: true }, serviceId);
      show("Booking cancelled");
      loadAll();
    } catch (e) {
      show("Could not cancel booking", "error");
    }
  };

  const archiveType = async (type, isActive) => {
    try {
      await AppointmentService.updateType(type.id, { ...type, is_active: isActive }, serviceId);
      show(isActive ? "Appointment service restored" : "Appointment service archived");
      loadAll();
    } catch (e) {
      show("Could not update appointment service", "error");
    }
  };

  const archiveResource = async (resource, isActive) => {
    try {
      await AppointmentService.updateResource(
        resource.id,
        {
          ...resource,
          is_active: isActive,
          appointment_type_ids: asArray(resource.appointment_type_ids),
        },
        serviceId
      );
      show(isActive ? "Resource restored" : "Resource archived");
      loadAll();
    } catch (e) {
      show("Could not update resource", "error");
    }
  };

  const saveReschedule = async () => {
    if (!rescheduleForm.id || !rescheduleForm.starts_at_utc) return;
    try {
      await AppointmentService.rescheduleBooking(
        rescheduleForm.id,
        {
          starts_at_utc: new Date(rescheduleForm.starts_at_utc).toISOString(),
          resource_id: rescheduleForm.resource_id || null,
        },
        serviceId
      );
      setRescheduleForm({ id: null, starts_at_utc: "", resource_id: "" });
      show("Booking rescheduled");
      loadAll();
    } catch (e) {
      show("Could not reschedule booking", "error");
    }
  };

  const calendarDays = useMemo(() => {
    const anchor = new Date(`${calendarDate}T00:00:00`);
    const base = calendarView === "month"
      ? startOfWeek(startOfMonth(anchor))
      : calendarView === "week"
        ? startOfWeek(anchor)
        : anchor;
    const length = calendarView === "month" ? 42 : calendarView === "week" ? 7 : 1;
    return Array.from({ length }, (_, idx) => {
      const d = addDays(base, idx);
      return {
        key: dateInputValue(d),
        label: d.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" }),
        compactLabel: d.toLocaleDateString(undefined, { day: "numeric" }),
        isToday: dateInputValue(d) === dateInputValue(new Date()),
        isOutsideMonth: calendarView === "month" && d.getMonth() !== anchor.getMonth(),
        date: d,
      };
    });
  }, [calendarDate, calendarView]);

  const calendarTitle = useMemo(() => {
    const anchor = new Date(`${calendarDate}T00:00:00`);
    if (calendarView === "day") {
      return anchor.toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric", year: "numeric" });
    }
    if (calendarView === "month") {
      return anchor.toLocaleDateString(undefined, { month: "long", year: "numeric" });
    }
    const start = startOfWeek(anchor);
    const end = addDays(start, 6);
    return `${start.toLocaleDateString(undefined, { month: "short", day: "numeric" })} - ${end.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}`;
  }, [calendarDate, calendarView]);

  const moveCalendar = (direction) => {
    const anchor = new Date(`${calendarDate}T00:00:00`);
    const next = new Date(anchor);
    if (calendarView === "month") {
      next.setMonth(next.getMonth() + direction);
    } else {
      next.setDate(next.getDate() + direction * (calendarView === "week" ? 7 : 1));
    }
    setCalendarDate(dateInputValue(next));
  };

  const getBookingLabel = (booking) => {
    const type = types.find((t) => Number(t.id) === Number(booking.appointment_type_id));
    const resource = resources.find((r) => Number(r.id) === Number(booking.resource_id));
    return {
      typeName: type?.name || `Type ${booking.appointment_type_id}`,
      resourceName: resource?.name || (booking.resource_id ? `Resource ${booking.resource_id}` : "Any resource"),
    };
  };

  const renderBookingCard = (booking, viewMode = "full") => {
    const labels = getBookingLabel(booking);
    const cancelled = String(booking.status || "").startsWith("cancelled");
    const movable = !cancelled && canManageBooking(booking);
    const timeLabel = booking.starts_at_utc
      ? new Date(booking.starts_at_utc).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
      : "--:--";
    const statusShort = String(booking.status || "").replace(/_/g, " ");

    if (viewMode === "month") {
      return (
        <Box
          key={booking.id}
          draggable={movable}
          onDragStart={(e) => e.dataTransfer.setData("text/booking-id", String(booking.id))}
          title={`${timeLabel} • ${labels.typeName} • ${formatBookingContact(booking)} • ${booking.status}`}
          sx={{
            px: 0.6,
            py: 0.35,
            borderRadius: 1,
            bgcolor: cancelled ? "action.disabledBackground" : "rgba(25, 118, 210, 0.08)",
            border: "1px solid",
            borderColor: cancelled ? "action.disabled" : "rgba(25, 118, 210, 0.22)",
            cursor: movable ? "grab" : "default",
            display: "flex",
            alignItems: "center",
            gap: 0.5,
            minWidth: 0,
            opacity: cancelled ? 0.72 : 1,
          }}
        >
          <Box
            sx={{
              width: 5,
              height: 5,
              borderRadius: "50%",
              flexShrink: 0,
              bgcolor: cancelled ? "text.disabled" : "primary.main",
            }}
          />
          <Typography
            variant="caption"
            noWrap
            sx={{ fontSize: "0.68rem", fontWeight: 600, lineHeight: 1.2, minWidth: 0, flex: 1 }}
          >
            {timeLabel} · {labels.typeName}
          </Typography>
        </Box>
      );
    }

    const dense = viewMode === "week";
    return (
      <Box
        key={booking.id}
        draggable={movable}
        onDragStart={(e) => e.dataTransfer.setData("text/booking-id", String(booking.id))}
        sx={{
          p: dense ? 0.75 : 1,
          borderRadius: 1.5,
          bgcolor: cancelled ? "action.disabledBackground" : "action.hover",
          cursor: movable ? "grab" : "default",
          border: "1px solid",
          borderColor: cancelled ? "action.disabled" : "divider",
          flexShrink: 0,
        }}
      >
        <Typography variant="caption" display="block" fontWeight={700} noWrap>
          {timeLabel} • {labels.typeName}
        </Typography>
        {!dense && (
          <Typography variant="caption" color="text.secondary" display="block" noWrap>
            {labels.resourceName}
          </Typography>
        )}
        <Typography variant="caption" color="text.secondary" display="block" noWrap title={formatBookingContact(booking)}>
          #{booking.id} • {formatBookingContact(booking)} • {statusShort}
        </Typography>
      </Box>
    );
  };

  const openCalendarDayView = (dayKey) => {
    setCalendarDate(dayKey);
    setCalendarView("day");
  };

  const moveBookingToDay = async (bookingId, dayKey) => {
    const booking = bookings.find((b) => Number(b.id) === Number(bookingId));
    if (!booking?.starts_at_utc) return;
    if (!canManageBooking(booking)) {
      show("You do not have permission to move this booking", "error");
      return;
    }
    const old = new Date(booking.starts_at_utc);
    const next = new Date(`${dayKey}T00:00:00`);
    next.setHours(old.getHours(), old.getMinutes(), 0, 0);
    try {
      await AppointmentService.rescheduleBooking(
        booking.id,
        {
          starts_at_utc: next.toISOString(),
          resource_id: booking.resource_id || null,
        },
        serviceId
      );
      show("Booking moved");
      loadAll();
    } catch (e) {
      show("Could not move booking", "error");
    }
  };

  const setDayOpen = (dayKey, open) => {
    setWeeklyHours((prev) => ({
      ...prev,
      [dayKey]: open ? (prev[dayKey]?.length ? prev[dayKey] : [{ from: "09:00", to: "17:00" }]) : [],
    }));
  };

  const setDayRange = (dayKey, idx, field, value) => {
    setWeeklyHours((prev) => ({
      ...prev,
      [dayKey]: (prev[dayKey] || []).map((r, i) => (i === idx ? { ...r, [field]: value } : r)),
    }));
  };

  const addDayRange = (dayKey) => {
    setWeeklyHours((prev) => ({
      ...prev,
      [dayKey]: [...(prev[dayKey] || []), { from: "09:00", to: "17:00" }],
    }));
  };

  const removeDayRange = (dayKey, idx) => {
    setWeeklyHours((prev) => ({
      ...prev,
      [dayKey]: (prev[dayKey] || []).filter((_, i) => i !== idx),
    }));
  };

  const setException = (idx, patch) => {
    setExceptions((prev) => prev.map((x, i) => (i === idx ? { ...x, ...patch } : x)));
  };

  const setExceptionRange = (exIdx, rangeIdx, field, value) => {
    setExceptions((prev) => prev.map((ex, i) => {
      if (i !== exIdx) return ex;
      const ranges = ex.hours?.length ? ex.hours : [{ from: "09:00", to: "17:00" }];
      return {
        ...ex,
        hours: ranges.map((r, ri) => (ri === rangeIdx ? { ...r, [field]: value } : r)),
      };
    }));
  };

  const setRecurringRule = (idx, patch) => {
    setRecurringRules((prev) => prev.map((rule, i) => (i === idx ? { ...rule, ...patch } : rule)));
  };

  const setRecurringRuleRange = (ruleIdx, rangeIdx, field, value) => {
    setRecurringRules((prev) => prev.map((rule, i) => {
      if (i !== ruleIdx) return rule;
      const ranges = rule.hours?.length ? rule.hours : [{ from: "09:00", to: "17:00" }];
      return {
        ...rule,
        hours: ranges.map((r, ri) => (ri === rangeIdx ? { ...r, [field]: value } : r)),
      };
    }));
  };

  const setBlackoutTemplate = (idx, patch) => {
    setBlackoutTemplates((prev) => prev.map((rule, i) => (i === idx ? { ...rule, ...patch } : rule)));
  };

  const selectedTypeForPreview = types.find((t) => String(t.id) === String(chatbotPreview.appointment_type_id));
  const selectedResourceForPreview = resources.find((r) => String(r.id) === String(chatbotPreview.resource_id));
  const availabilityPayloadPreview = {
    appointment_type_id: Number(chatbotPreview.appointment_type_id || 0) || "<appointment_type_id>",
    resource_id: chatbotPreview.resource_id ? Number(chatbotPreview.resource_id) : null,
    range_start_utc: new Date().toISOString(),
    range_end_utc: new Date(Date.now() + 7 * 24 * 3600 * 1000).toISOString(),
    slot_step_minutes: selectedTypeForPreview?.duration_minutes || 30,
  };

  const registryActions = asArray(registry?.actions).filter((a) =>
    String(a.id || "").startsWith("appointment.")
      || String(a.id || "").startsWith("flow_session.")
      || a.id === "checkout.send_native_review_pay"
  );

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1440, width: "100%", mx: "auto" }}>
      <Box
        sx={{
          mb: 2,
          p: { xs: 2, md: 2.5 },
          borderRadius: 3,
          border: "1px solid rgba(15,23,42,0.08)",
          background: "linear-gradient(135deg, #ffffff 0%, #f8fbff 100%)",
          boxShadow: "0 12px 30px rgba(15,23,42,0.05)",
        }}
      >
        <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" gap={2}>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 850, letterSpacing: "-0.02em" }}>
              Appointment Operations
            </Typography>
            <Typography color="text.secondary" sx={{ mt: 0.5 }}>
              Manage appointment services, staff resources, calendars, and customer bookings for WhatsApp flows.
            </Typography>
          </Box>
          <Button
            size="small"
            variant="outlined"
            onClick={loadAll}
            disabled={loading}
            sx={{ alignSelf: { xs: "flex-start", md: "center" }, borderRadius: 2, px: 2 }}
          >
            Refresh
          </Button>
        </Stack>
      </Box>

      <Box sx={{ borderBottom: "1px solid rgba(15,23,42,0.10)", mb: 2 }}>
        <Tabs value={tab} onChange={(_, v) => setTab(v)}>
          <Tab value="types" label="Services" />
          <Tab value="resources" label="Resources" />
          <Tab value="availability" label="Availability" />
          <Tab value="bookings" label="Bookings" />
          {/* <Tab value="chatbot" label="Chatbot data" /> */}
        </Tabs>
      </Box>

      {tab === "types" && (
        <SectionCard
          title="Appointment services"
          subtitle="Create the services customers can book. Long lists stay inside this panel."
          action={(
            <Button
              variant="contained"
              onClick={() => {
                setTypeForm(emptyType);
                setTypeDialogOpen(true);
              }}
              sx={{ borderRadius: 2, whiteSpace: "nowrap" }}
            >
              New service
            </Button>
          )}
          sx={{ minHeight: 360 }}
        >
          <Stack direction={{ xs: "column", md: "row" }} spacing={1.25} sx={{ mb: 2 }}>
            <TextField
              label="Search service"
              size="small"
              value={typeFilters.search}
              onChange={(e) => setTypeFilters({ ...typeFilters, search: e.target.value })}
              sx={{ minWidth: { xs: "100%", md: 280 } }}
            />
            <TextField
              select
              label="Status"
              size="small"
              value={typeFilters.status}
              onChange={(e) => setTypeFilters({ ...typeFilters, status: e.target.value })}
              sx={{ minWidth: { xs: "100%", md: 180 } }}
            >
              <MenuItem value="">All statuses</MenuItem>
              <MenuItem value="active">Active</MenuItem>
              <MenuItem value="archived">Archived</MenuItem>
            </TextField>
            <Button variant="text" onClick={() => setTypeFilters({ search: "", status: "" })} sx={{ alignSelf: { md: "center" } }}>
              Clear
            </Button>
          </Stack>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            Showing {visibleTypes.length} of {filteredTypes.length} filtered records ({types.length} total)
          </Typography>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                sm: "repeat(auto-fill, minmax(300px, 360px))",
              },
              justifyContent: "start",
              alignItems: "stretch",
              gap: 1.5,
              maxHeight: 620,
              overflowY: "auto",
              pr: 0.5,
            }}
          >
            {visibleTypes.map((t) => {
              const archived = t.is_active === false || t.is_active === 0;
              return (
                <Box
                  key={t.id}
                  sx={{
                    p: 1.75,
                    borderRadius: 2.5,
                    border: "1px solid rgba(15,23,42,0.10)",
                    bgcolor: "#fff",
                    minHeight: 150,
                    transition: "0.16s ease",
                    "&:hover": {
                      borderColor: "rgba(25,118,210,0.35)",
                      boxShadow: "0 10px 24px rgba(15,23,42,0.08)",
                    },
                  }}
                >
                  <Stack justifyContent="space-between" gap={1.5} sx={{ height: "100%" }}>
                    <Box sx={{ minWidth: 0 }}>
                      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.75 }}>
                        <Chip size="small" label={archived ? "Archived" : "Active"} color={archived ? "default" : "success"} />
                        {t.code && <Chip size="small" variant="outlined" label={t.code} />}
                      </Stack>
                      <Typography sx={{ fontWeight: 800, color: "#111827" }}>{t.name}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        {Number(t.duration_minutes || 0)
                          + Number(t.buffer_before_minutes || 0)
                          + Number(t.buffer_after_minutes || 0)}{" "}
                        min blocked on calendar
                        {Number(t.buffer_before_minutes || 0) || Number(t.buffer_after_minutes || 0)
                          ? ` (${t.duration_minutes} min visit + ${Number(t.buffer_before_minutes || 0) + Number(t.buffer_after_minutes || 0)} min gap)`
                          : ` (${t.duration_minutes} min visit)`}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {formatMoney(t.currency, t.price_minor)}
                        {t.deposit_minor ? ` • Deposit ${formatMoney(t.currency, t.deposit_minor)}` : ""}
                      </Typography>
                    </Box>
                    <Stack direction="row" spacing={0.75} alignItems="center" justifyContent="flex-end" flexWrap="wrap">
                      <Button size="small" onClick={() => {
                        setTypeForm({
                          ...emptyType,
                          ...t,
                          price_minor: majorFromMinor(t.price_minor),
                          deposit_minor: majorFromMinor(t.deposit_minor),
                        });
                        setTypeDialogOpen(true);
                      }}>
                        Edit
                      </Button>
                      <Button size="small" color={archived ? "success" : "warning"} onClick={() => archiveType(t, archived)}>
                        {archived ? "Restore" : "Archive"}
                      </Button>
                    </Stack>
                  </Stack>
                </Box>
              );
            })}
            {!visibleTypes.length && (
              <Box sx={{ p: 3, textAlign: "center", border: "1px dashed rgba(15,23,42,0.18)", borderRadius: 2.5 }}>
                <Typography sx={{ fontWeight: 700 }}>No matching appointment services</Typography>
                <Typography variant="body2" color="text.secondary">
                  Adjust filters or use New service to create a bookable service.
                </Typography>
              </Box>
            )}
          </Box>
          {typePageCount > 1 && (
            <Stack direction="row" justifyContent="flex-end" sx={{ mt: 2 }}>
              <Pagination
                count={typePageCount}
                page={currentTypePage}
                onChange={(_, page) => setTypePage(page)}
                color="primary"
              />
            </Stack>
          )}
        </SectionCard>
      )}

      <Dialog open={typeDialogOpen} onClose={() => setTypeDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 800 }}>
          {typeForm.id ? "Edit appointment service" : "New appointment service"}
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <TextField label="Service name" value={typeForm.name} onChange={(e) => setTypeForm({ ...typeForm, name: e.target.value })} />
            <TextField
              label="Appointment length (minutes)"
              type="number"
              value={typeForm.duration_minutes}
              onChange={(e) => setTypeForm({ ...typeForm, duration_minutes: e.target.value })}
              helperText="How long the visit or session lasts (what you tell the customer)."
            />
            <Alert severity="info" sx={{ py: 0.5 }}>
              <Typography variant="body2" sx={{ mb: 1 }}>
                <strong>Gap time (optional)</strong> — extra minutes blocked on the calendar so appointments are not booked back-to-back.
                Leave both at 0 if you do not need a gap.
              </Typography>
              <Typography variant="caption" color="text.secondary" component="p">
                Example: 30 min visit + 10 min prep + 10 min cleanup → each booking blocks{" "}
                <strong>
                  {Number(typeForm.duration_minutes || 0)
                    + Number(typeForm.buffer_before_minutes || 0)
                    + Number(typeForm.buffer_after_minutes || 0)}{" "}
                  minutes
                </strong>{" "}
                total on the schedule.
              </Typography>
            </Alert>
            <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
              <TextField
                label="Prep / setup time (minutes)"
                type="number"
                value={typeForm.buffer_before_minutes}
                onChange={(e) => setTypeForm({ ...typeForm, buffer_before_minutes: e.target.value })}
                fullWidth
                helperText="Time needed before the visit starts (e.g. room setup, review notes)."
              />
              <TextField
                label="Cleanup / wrap-up time (minutes)"
                type="number"
                value={typeForm.buffer_after_minutes}
                onChange={(e) => setTypeForm({ ...typeForm, buffer_after_minutes: e.target.value })}
                fullWidth
                helperText="Time needed after the visit ends (e.g. sanitizing, writing notes)."
              />
            </Stack>
            <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
              <TextField
                label="Price (INR)"
                type="number"
                inputProps={{ min: 0, step: 0.01 }}
                value={typeForm.price_minor}
                onChange={(e) => setTypeForm({ ...typeForm, price_minor: e.target.value })}
                fullWidth
              />
              <TextField
                label="Deposit (INR)"
                type="number"
                inputProps={{ min: 0, step: 0.01 }}
                value={typeForm.deposit_minor}
                onChange={(e) => setTypeForm({ ...typeForm, deposit_minor: e.target.value })}
                fullWidth
              />
              <TextField label="Currency" value={typeForm.currency} onChange={(e) => setTypeForm({ ...typeForm, currency: e.target.value.toUpperCase().slice(0, 3) })} fullWidth />
            </Stack>
            {typeForm.id && (
              <TextField
                label="Reference code"
                value={typeForm.code}
                onChange={(e) => setTypeForm({ ...typeForm, code: e.target.value })}
                helperText="Optional. Used internally for reports and developer references."
              />
            )}
            <FormControlLabel
              control={<Switch checked={typeForm.is_active !== false && typeForm.is_active !== 0} onChange={(e) => setTypeForm({ ...typeForm, is_active: e.target.checked })} />}
              label="Active"
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setTypeDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={saveType}>{typeForm.id ? "Save changes" : "Create service"}</Button>
        </DialogActions>
      </Dialog>

      {tab === "resources" && (
        <SectionCard
          title="Appointment resources"
          subtitle="Doctors, staff, rooms, chairs, consultants, or any bookable resource."
          action={(
            <Button
              variant="contained"
              onClick={() => {
                setResourceForm(emptyResource);
                setResourceDialogOpen(true);
              }}
              sx={{ borderRadius: 2, whiteSpace: "nowrap" }}
            >
              New resource
            </Button>
          )}
          sx={{ minHeight: 360 }}
        >
          <Stack direction={{ xs: "column", lg: "row" }} spacing={1.25} sx={{ mb: 2 }}>
            <TextField
              label="Search resource, staff, service"
              size="small"
              value={resourceFilters.search}
              onChange={(e) => setResourceFilters({ ...resourceFilters, search: e.target.value })}
              sx={{ minWidth: { xs: "100%", lg: 280 } }}
            />
            <TextField
              select
              label="Status"
              size="small"
              value={resourceFilters.status}
              onChange={(e) => setResourceFilters({ ...resourceFilters, status: e.target.value })}
              sx={{ minWidth: { xs: "100%", lg: 170 } }}
            >
              <MenuItem value="">All statuses</MenuItem>
              <MenuItem value="active">Active</MenuItem>
              <MenuItem value="archived">Archived</MenuItem>
            </TextField>
            <TextField
              select
              label="Service"
              size="small"
              value={resourceFilters.service_id}
              onChange={(e) => setResourceFilters({ ...resourceFilters, service_id: e.target.value })}
              sx={{ minWidth: { xs: "100%", lg: 220 } }}
            >
              <MenuItem value="">All services</MenuItem>
              {types.map((type) => (
                <MenuItem key={type.id} value={String(type.id)}>{type.name}</MenuItem>
              ))}
            </TextField>
            <TextField
              select
              label="Staff"
              size="small"
              value={resourceFilters.staff_id}
              onChange={(e) => setResourceFilters({ ...resourceFilters, staff_id: e.target.value })}
              sx={{ minWidth: { xs: "100%", lg: 220 } }}
            >
              <MenuItem value="">All staff</MenuItem>
              {staff.map((member) => {
                const id = member.id || member.account_id;
                return <MenuItem key={id} value={String(id)}>{staffLabel(id)}</MenuItem>;
              })}
            </TextField>
            <Button
              variant="text"
              onClick={() => setResourceFilters({ search: "", status: "", service_id: "", staff_id: "" })}
              sx={{ alignSelf: { lg: "center" } }}
            >
              Clear
            </Button>
          </Stack>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            Showing {visibleResources.length} of {filteredResources.length} filtered records ({resources.length} total)
          </Typography>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                sm: "repeat(auto-fill, minmax(320px, 390px))",
              },
              justifyContent: "start",
              alignItems: "stretch",
              gap: 1.5,
              maxHeight: 620,
              overflowY: "auto",
              pr: 0.5,
            }}
          >
            {visibleResources.map((r) => {
              const archived = r.is_active === false || r.is_active === 0;
              const staffAccess = getResourceStaffAccess(r);
              const calendar = getResourceExternalCalendar(r);
              const serviceNames = asArray(r.appointment_type_ids).length
                ? asArray(r.appointment_type_ids)
                  .map((id) => types.find((t) => Number(t.id) === Number(id))?.name || id)
                  .join(", ")
                : "All services";
              return (
                <Box
                  key={r.id}
                  sx={{
                    p: 1.75,
                    borderRadius: 2.5,
                    border: "1px solid rgba(15,23,42,0.10)",
                    bgcolor: "#fff",
                    minHeight: 172,
                    transition: "0.16s ease",
                    "&:hover": {
                      borderColor: "rgba(25,118,210,0.35)",
                      boxShadow: "0 10px 24px rgba(15,23,42,0.08)",
                    },
                  }}
                >
                  <Stack justifyContent="space-between" gap={1.5} sx={{ height: "100%" }}>
                    <Box sx={{ minWidth: 0 }}>
                      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.75 }}>
                        <Chip size="small" label={archived ? "Archived" : "Active"} color={archived ? "default" : "success"} />
                        {r.code && <Chip size="small" variant="outlined" label={r.code} />}
                      </Stack>
                      <Typography sx={{ fontWeight: 800, color: "#111827" }}>{r.name}</Typography>
                      <Typography variant="body2" color="text.secondary">{r.timezone}</Typography>
                      <Typography variant="caption" color="text.secondary" display="block">
                        Services: {serviceNames}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" display="block">
                        Staff: {staffAccess.staff_account_ids.length
                          ? staffAccess.staff_account_ids.map(staffLabel).join(", ")
                          : "Unassigned"}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" display="block">
                        Calendar: {calendar.provider === "none"
                          ? "Not connected"
                          : `${calendar.provider} (${calendar.sync_direction})`}
                      </Typography>
                    </Box>
                    <Stack direction="row" spacing={0.75} alignItems="center" justifyContent="flex-end" flexWrap="wrap">
                      <Button size="small" variant="contained" onClick={() => { setSelectedResourceId(String(r.id)); setTab("availability"); }}>
                        Hours
                      </Button>
                      <Button size="small" onClick={() => { setResourceForm({ ...emptyResource, ...r }); setResourceDialogOpen(true); }}>
                        Edit
                      </Button>
                      <Button size="small" color={archived ? "success" : "warning"} onClick={() => archiveResource(r, archived)}>
                        {archived ? "Restore" : "Archive"}
                      </Button>
                    </Stack>
                  </Stack>
                </Box>
              );
            })}
            {!visibleResources.length && (
              <Box sx={{ p: 3, textAlign: "center", border: "1px dashed rgba(15,23,42,0.18)", borderRadius: 2.5 }}>
                <Typography sx={{ fontWeight: 700 }}>No matching resources</Typography>
                <Typography variant="body2" color="text.secondary">
                  Adjust filters or use New resource to create staff, rooms, or bookable assets.
                </Typography>
              </Box>
            )}
          </Box>
          {resourcePageCount > 1 && (
            <Stack direction="row" justifyContent="flex-end" sx={{ mt: 2 }}>
              <Pagination
                count={resourcePageCount}
                page={currentResourcePage}
                onChange={(_, page) => setResourcePage(page)}
                color="primary"
              />
            </Stack>
          )}
        </SectionCard>
      )}

      <Dialog open={resourceDialogOpen} onClose={() => setResourceDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ fontWeight: 800 }}>
          {resourceForm.id ? "Edit resource" : "New resource"}
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <TextField label="Resource name" value={resourceForm.name} onChange={(e) => setResourceForm({ ...resourceForm, name: e.target.value })} helperText="Example: Dr Kumar, Room 1, Chair 3, Consultation team" />
            <TextField label="Timezone" value={resourceForm.timezone} onChange={(e) => setResourceForm({ ...resourceForm, timezone: e.target.value })} helperText="Example: Asia/Kolkata" />
            <TextField
              select
              label="Assigned appointment services"
              value={resourceForm.appointment_type_ids || []}
              onChange={(e) => setResourceForm({ ...resourceForm, appointment_type_ids: normalizeIdArray(e.target.value) })}
              SelectProps={{ multiple: true }}
              helperText="Leave empty to allow this resource for all appointment services."
            >
              {types.map((t) => (
                <MenuItem key={t.id} value={t.id}>
                  {t.name} ({t.code})
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              label="Assigned staff calendars"
              value={resourceForm.staff_account_ids || []}
              onChange={(e) => setResourceForm({ ...resourceForm, staff_account_ids: normalizeStringArray(e.target.value) })}
              SelectProps={{ multiple: true }}
              helperText="Used for personal staff calendar filtering and staff-level booking permissions."
            >
              {staff.map((member) => {
                const id = member.id || member.account_id;
                return (
                  <MenuItem key={id} value={id}>
                    {staffLabel(id)} ({member.role || "staff"})
                  </MenuItem>
                );
              })}
            </TextField>
            <Stack direction={{ xs: "column", md: "row" }} spacing={1}>
              <TextField
                select
                label="Calendar visibility"
                value={resourceForm.calendar_visibility}
                onChange={(e) => setResourceForm({ ...resourceForm, calendar_visibility: e.target.value })}
                fullWidth
              >
                <MenuItem value="team">Visible on team calendar</MenuItem>
                <MenuItem value="assigned_staff">Assigned staff calendar only</MenuItem>
                <MenuItem value="admin_only">Admin only</MenuItem>
              </TextField>
              <TextField
                select
                label="Booking changes"
                value={resourceForm.booking_manage_permission}
                onChange={(e) => setResourceForm({ ...resourceForm, booking_manage_permission: e.target.value })}
                fullWidth
              >
                <MenuItem value="admin_or_assigned">Admin or assigned staff</MenuItem>
                <MenuItem value="assigned_staff_only">Assigned staff only</MenuItem>
                <MenuItem value="admin_only">Admin only</MenuItem>
              </TextField>
            </Stack>
            <Divider />
            <Typography variant="subtitle2">External calendar sync</Typography>
            <Stack direction={{ xs: "column", md: "row" }} spacing={1}>
              <TextField
                select
                label="Provider"
                value={resourceForm.external_calendar_provider}
                onChange={(e) => setResourceForm({ ...resourceForm, external_calendar_provider: e.target.value })}
                fullWidth
              >
                <MenuItem value="none">Not connected</MenuItem>
                <MenuItem value="google">Google Calendar</MenuItem>
                <MenuItem value="ical">External iCal</MenuItem>
                <MenuItem value="outlook">Outlook / Microsoft 365</MenuItem>
              </TextField>
              <TextField
                label="Calendar ID / URL"
                value={resourceForm.external_calendar_id}
                onChange={(e) => setResourceForm({ ...resourceForm, external_calendar_id: e.target.value })}
                disabled={resourceForm.external_calendar_provider === "none"}
                fullWidth
                helperText="Google calendar ID, shared iCal URL, or external calendar reference."
              />
            </Stack>
            <Stack direction={{ xs: "column", md: "row" }} spacing={1}>
              <TextField
                select
                label="Sync direction"
                value={resourceForm.external_calendar_sync_direction}
                onChange={(e) => setResourceForm({ ...resourceForm, external_calendar_sync_direction: e.target.value })}
                disabled={resourceForm.external_calendar_provider === "none"}
                fullWidth
              >
                <MenuItem value="two_way">Two-way</MenuItem>
                <MenuItem value="import_busy">Import busy time only</MenuItem>
                <MenuItem value="export_bookings">Export bookings only</MenuItem>
              </TextField>
              <FormControlLabel
                control={<Switch checked={resourceForm.external_calendar_busy_only !== false} onChange={(e) => setResourceForm({ ...resourceForm, external_calendar_busy_only: e.target.checked })} />}
                label="Import as busy blocks only"
              />
            </Stack>
            {resourceForm.id && (
              <TextField
                label="Reference code"
                value={resourceForm.code}
                onChange={(e) => setResourceForm({ ...resourceForm, code: e.target.value })}
                helperText="Optional. Used internally for reports and developer references."
              />
            )}
            <FormControlLabel
              control={<Switch checked={resourceForm.is_active !== false && resourceForm.is_active !== 0} onChange={(e) => setResourceForm({ ...resourceForm, is_active: e.target.checked })} />}
              label="Active"
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setResourceDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={saveResource}>{resourceForm.id ? "Save changes" : "Create resource"}</Button>
        </DialogActions>
      </Dialog>

      {tab === "availability" && (
        <SectionCard
          title="Working hours"
          subtitle="Simple weekly hours plus JSON exceptions. Availability action nodes read this before showing slots."
          action={<Button variant="contained" onClick={saveAvailability}>Save hours</Button>}
        >
          <Stack spacing={2}>
            <TextField
              select
              label="Resource"
              value={selectedResourceId}
              onChange={(e) => setSelectedResourceId(e.target.value)}
              sx={{ maxWidth: 360 }}
            >
              {resources.map((r) => (
                <MenuItem key={r.id} value={String(r.id)}>{r.name}</MenuItem>
              ))}
            </TextField>
            <Grid container spacing={1}>
              {dayLabels.map(([key, label]) => {
                const open = weeklyHours[key]?.length > 0;
                const ranges = weeklyHours[key] || [];
                return (
                  <Grid item xs={12} md={6} lg={4} key={key}>
                    <Card variant="outlined">
                      <CardContent>
                        <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
                          <Typography fontWeight={600}>{label}</Typography>
                          <Button size="small" onClick={() => setDayOpen(key, !open)}>
                            {open ? "Close" : "Open"}
                          </Button>
                        </Stack>
                        <Stack spacing={1}>
                          {ranges.map((range, idx) => (
                            <Stack direction="row" spacing={1} key={`${key}-${idx}`}>
                              <TextField size="small" type="time" label="From" value={range.from} disabled={!open} onChange={(e) => setDayRange(key, idx, "from", e.target.value)} />
                              <TextField size="small" type="time" label="To" value={range.to} disabled={!open} onChange={(e) => setDayRange(key, idx, "to", e.target.value)} />
                              <Button size="small" color="error" disabled={!open || ranges.length <= 1} onClick={() => removeDayRange(key, idx)}>Remove</Button>
                            </Stack>
                          ))}
                          {open && <Button size="small" onClick={() => addDayRange(key)}>Add shift</Button>}
                        </Stack>
                      </CardContent>
                    </Card>
                  </Grid>
                );
              })}
            </Grid>
            <Divider />
            <Stack spacing={1}>
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Box>
                  <Typography variant="subtitle1" fontWeight={700}>Exceptions</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Add holidays or custom hours for specific dates.
                  </Typography>
                </Box>
                <Button onClick={() => setExceptions((prev) => [...prev, { ...emptyException }])}>Add exception</Button>
              </Stack>
              {exceptions.map((ex, exIdx) => (
                <Card variant="outlined" key={`${ex.date || "new"}-${exIdx}`}>
                  <CardContent>
                    <Stack spacing={1}>
                      <Stack direction={{ xs: "column", md: "row" }} spacing={1} alignItems={{ md: "center" }}>
                        <TextField
                          type="date"
                          label="Date"
                          value={ex.date || ""}
                          onChange={(e) => setException(exIdx, { date: e.target.value })}
                          InputLabelProps={{ shrink: true }}
                        />
                        <FormControlLabel
                          control={<Switch checked={ex.closed === true} onChange={(e) => setException(exIdx, { closed: e.target.checked, ...(e.target.checked ? {} : { hours: ex.hours?.length ? ex.hours : [{ from: "09:00", to: "17:00" }] }) })} />}
                          label="Closed"
                        />
                        <Button color="error" onClick={() => setExceptions((prev) => prev.filter((_, i) => i !== exIdx))}>Remove date</Button>
                      </Stack>
                      {ex.closed !== true && (
                        <Stack spacing={1}>
                          {(ex.hours || [{ from: "09:00", to: "17:00" }]).map((range, rangeIdx) => (
                            <Stack direction="row" spacing={1} key={rangeIdx}>
                              <TextField size="small" type="time" label="From" value={range.from} onChange={(e) => setExceptionRange(exIdx, rangeIdx, "from", e.target.value)} />
                              <TextField size="small" type="time" label="To" value={range.to} onChange={(e) => setExceptionRange(exIdx, rangeIdx, "to", e.target.value)} />
                              <Button size="small" color="error" onClick={() => setException(exIdx, { hours: (ex.hours || []).filter((_, i) => i !== rangeIdx) })}>Remove</Button>
                            </Stack>
                          ))}
                          <Button size="small" onClick={() => setException(exIdx, { hours: [...(ex.hours || []), { from: "09:00", to: "17:00" }] })}>Add hours</Button>
                        </Stack>
                      )}
                    </Stack>
                  </CardContent>
                </Card>
              ))}
              {!exceptions.length && (
                <Typography variant="body2" color="text.secondary">No date exceptions configured.</Typography>
              )}
            </Stack>
            <Divider />
            <Stack spacing={1}>
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Box>
                  <Typography variant="subtitle1" fontWeight={700}>Recurring rules</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Seasonal hours or repeating closures layered above the weekly schedule.
                  </Typography>
                </Box>
                <Button onClick={() => setRecurringRules((prev) => [...prev, { ...emptyRecurringRule }])}>Add recurring rule</Button>
              </Stack>
              {recurringRules.map((rule, ruleIdx) => (
                <Card variant="outlined" key={`${rule.name || "rule"}-${ruleIdx}`}>
                  <CardContent>
                    <Stack spacing={1}>
                      <Stack direction={{ xs: "column", md: "row" }} spacing={1}>
                        <TextField label="Name" value={rule.name || ""} onChange={(e) => setRecurringRule(ruleIdx, { name: e.target.value })} />
                        <TextField type="date" label="Starts" value={rule.start_date || ""} onChange={(e) => setRecurringRule(ruleIdx, { start_date: e.target.value })} InputLabelProps={{ shrink: true }} />
                        <TextField type="date" label="Ends" value={rule.end_date || ""} onChange={(e) => setRecurringRule(ruleIdx, { end_date: e.target.value })} InputLabelProps={{ shrink: true }} />
                      </Stack>
                      <TextField
                        select
                        label="Days"
                        value={asArray(rule.days)}
                        onChange={(e) => setRecurringRule(ruleIdx, { days: normalizeStringArray(e.target.value) })}
                        SelectProps={{ multiple: true }}
                      >
                        {dayLabels.map(([key, label]) => <MenuItem key={key} value={key}>{label}</MenuItem>)}
                      </TextField>
                      <Stack direction={{ xs: "column", md: "row" }} spacing={1} alignItems={{ md: "center" }}>
                        <FormControlLabel control={<Switch checked={rule.enabled !== false} onChange={(e) => setRecurringRule(ruleIdx, { enabled: e.target.checked })} />} label="Enabled" />
                        <FormControlLabel control={<Switch checked={rule.closed === true} onChange={(e) => setRecurringRule(ruleIdx, { closed: e.target.checked })} />} label="Closed" />
                        <Button color="error" onClick={() => setRecurringRules((prev) => prev.filter((_, i) => i !== ruleIdx))}>Remove rule</Button>
                      </Stack>
                      {rule.closed !== true && (
                        <Stack spacing={1}>
                          {(rule.hours || [{ from: "09:00", to: "17:00" }]).map((range, rangeIdx) => (
                            <Stack direction="row" spacing={1} key={rangeIdx}>
                              <TextField size="small" type="time" label="From" value={range.from} onChange={(e) => setRecurringRuleRange(ruleIdx, rangeIdx, "from", e.target.value)} />
                              <TextField size="small" type="time" label="To" value={range.to} onChange={(e) => setRecurringRuleRange(ruleIdx, rangeIdx, "to", e.target.value)} />
                              <Button size="small" color="error" onClick={() => setRecurringRule(ruleIdx, { hours: (rule.hours || []).filter((_, i) => i !== rangeIdx) })}>Remove</Button>
                            </Stack>
                          ))}
                          <Button size="small" onClick={() => setRecurringRule(ruleIdx, { hours: [...(rule.hours || []), { from: "09:00", to: "17:00" }] })}>Add hours</Button>
                        </Stack>
                      )}
                    </Stack>
                  </CardContent>
                </Card>
              ))}
              {!recurringRules.length && <Typography variant="body2" color="text.secondary">No recurring rules configured.</Typography>}
            </Stack>
            <Divider />
            <Stack spacing={1}>
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Box>
                  <Typography variant="subtitle1" fontWeight={700}>Blackouts and holidays</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Block date ranges and optionally close fixed regional holidays.
                  </Typography>
                </Box>
                <Button onClick={() => setBlackoutTemplates((prev) => [...prev, { ...emptyBlackoutTemplate }])}>Add blackout</Button>
              </Stack>
              <TextField select label="Holiday region" value={holidayRegion} onChange={(e) => setHolidayRegion(e.target.value)} sx={{ maxWidth: 320 }}>
                <MenuItem value="">Do not apply regional holidays</MenuItem>
                <MenuItem value="IN">India fixed holidays</MenuItem>
                <MenuItem value="US">United States fixed holidays</MenuItem>
                <MenuItem value="AE">United Arab Emirates fixed holidays</MenuItem>
                <MenuItem value="GB">United Kingdom fixed holidays</MenuItem>
              </TextField>
              {blackoutTemplates.map((rule, idx) => (
                <Stack key={`${rule.name || "blackout"}-${idx}`} direction={{ xs: "column", md: "row" }} spacing={1} alignItems={{ md: "center" }}>
                  <TextField label="Name" value={rule.name || ""} onChange={(e) => setBlackoutTemplate(idx, { name: e.target.value })} />
                  <TextField type="date" label="Starts" value={rule.start_date || ""} onChange={(e) => setBlackoutTemplate(idx, { start_date: e.target.value })} InputLabelProps={{ shrink: true }} />
                  <TextField type="date" label="Ends" value={rule.end_date || ""} onChange={(e) => setBlackoutTemplate(idx, { end_date: e.target.value })} InputLabelProps={{ shrink: true }} />
                  <FormControlLabel control={<Switch checked={rule.enabled !== false} onChange={(e) => setBlackoutTemplate(idx, { enabled: e.target.checked })} />} label="Enabled" />
                  <Button color="error" onClick={() => setBlackoutTemplates((prev) => prev.filter((_, i) => i !== idx))}>Remove</Button>
                </Stack>
              ))}
              {!blackoutTemplates.length && <Typography variant="body2" color="text.secondary">No blackout templates configured.</Typography>}
            </Stack>
          </Stack>
        </SectionCard>
      )}

      {tab === "bookings" && (
        <SectionCard title="Appointment bookings" subtitle="Operational booking dashboard for merchant follow-up.">
          <Stack spacing={2} sx={{ mb: 2 }}>
            <Stack direction={{ xs: "column", lg: "row" }} spacing={1} justifyContent="space-between" alignItems={{ lg: "center" }}>
              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                <Button variant={calendarView === "day" ? "contained" : "outlined"} onClick={() => setCalendarView("day")}>Day</Button>
                <Button variant={calendarView === "week" ? "contained" : "outlined"} onClick={() => setCalendarView("week")}>Week</Button>
                <Button variant={calendarView === "month" ? "contained" : "outlined"} onClick={() => setCalendarView("month")}>Month</Button>
                <Divider orientation="vertical" flexItem />
                <Button onClick={() => moveCalendar(-1)}>Previous</Button>
                <Button onClick={() => setCalendarDate(dateInputValue(new Date()))}>Today</Button>
                <Button onClick={() => moveCalendar(1)}>Next</Button>
              </Stack>
              <Stack direction={{ xs: "column", md: "row" }} spacing={1} alignItems={{ md: "center" }}>
                <Typography variant="subtitle1" fontWeight={700}>{calendarTitle}</Typography>
                <TextField
                  type="date"
                  label="Go to date"
                  size="small"
                  value={calendarDate}
                  onChange={(e) => setCalendarDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
                <TextField
                  select
                  label="Staff calendar"
                  size="small"
                  value={calendarStaffFilter}
                  onChange={(e) => setCalendarStaffFilter(e.target.value)}
                  sx={{ minWidth: 220 }}
                >
                  <MenuItem value="">Team calendar</MenuItem>
                  {staff.map((member) => {
                    const id = member.id || member.account_id;
                    return <MenuItem key={id} value={id}>{staffLabel(id)}</MenuItem>;
                  })}
                </TextField>
              </Stack>
            </Stack>
            <Alert severity="info">
              Drag a booking card onto another day to reschedule while preserving its time and resource.
            </Alert>
          </Stack>
          <Grid
            container
            spacing={calendarView === "month" ? 0.75 : 1}
            sx={{
              mb: 2,
              ...(calendarView === "month"
                ? { alignItems: "flex-start" }
                : {}),
            }}
          >
            {calendarDays.map((day) => {
              const rows = calendarBookings
                .filter((b) => b.starts_at_utc?.slice(0, 10) === day.key)
                .sort((a, b) => new Date(a.starts_at_utc || 0) - new Date(b.starts_at_utc || 0));
              const gridProps = calendarView === "day"
                ? { xs: 12 }
                : calendarView === "month"
                  ? { xs: 12 / 7 }
                  : { xs: 12, md: 6, lg: true };
              const isMonth = calendarView === "month";
              const visibleRows = isMonth ? rows.slice(0, MONTH_VISIBLE_BOOKINGS) : rows;
              const overflowCount = isMonth ? Math.max(0, rows.length - MONTH_VISIBLE_BOOKINGS) : 0;
              const cardViewMode = isMonth ? "month" : calendarView === "week" ? "week" : "full";

              return (
                <Grid item key={day.key} {...gridProps} sx={isMonth ? { display: "flex" } : undefined}>
                  <Card
                    variant="outlined"
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={(e) => {
                      e.preventDefault();
                      const id = e.dataTransfer.getData("text/booking-id");
                      if (id) moveBookingToDay(id, day.key);
                    }}
                    sx={{
                      width: "100%",
                      display: "flex",
                      flexDirection: "column",
                      minHeight: calendarView === "day" ? 360 : isMonth ? MONTH_CELL_HEIGHT_PX : 170,
                      ...(isMonth
                        ? {
                          height: MONTH_CELL_HEIGHT_PX,
                          maxHeight: MONTH_CELL_HEIGHT_PX,
                        }
                        : {}),
                      opacity: day.isOutsideMonth ? 0.55 : 1,
                      borderColor: day.isToday ? "primary.main" : "divider",
                      bgcolor: rows.length && isMonth ? "rgba(25,118,210,0.03)" : "background.paper",
                    }}
                  >
                    <CardContent
                      sx={{
                        p: isMonth ? 0.75 : 1.25,
                        flex: 1,
                        minHeight: 0,
                        display: "flex",
                        flexDirection: "column",
                        "&:last-child": { pb: isMonth ? 0.75 : 1.25 },
                      }}
                    >
                      <Stack
                        direction="row"
                        justifyContent="space-between"
                        alignItems="center"
                        spacing={0.5}
                        sx={{ flexShrink: 0 }}
                      >
                        <Stack direction="row" spacing={0.5} alignItems="center" sx={{ minWidth: 0 }}>
                          <Typography
                            variant={isMonth ? "body2" : "subtitle2"}
                            fontWeight={day.isToday ? 800 : 600}
                            color={day.isOutsideMonth ? "text.secondary" : "text.primary"}
                          >
                            {isMonth ? day.compactLabel : day.label}
                          </Typography>
                          {rows.length > 0 && isMonth && (
                            <Chip
                              size="small"
                              label={rows.length}
                              sx={{ height: 18, fontSize: "0.65rem", "& .MuiChip-label": { px: 0.6 } }}
                            />
                          )}
                        </Stack>
                        {day.isToday && (
                          <Chip
                            size="small"
                            color="primary"
                            label="Today"
                            sx={isMonth ? { height: 20, fontSize: "0.65rem" } : undefined}
                          />
                        )}
                      </Stack>
                      <Box
                        sx={{
                          mt: 0.75,
                          flex: 1,
                          minHeight: 0,
                          overflowY: isMonth ? "hidden" : "auto",
                          maxHeight: !isMonth && calendarView === "week"
                            ? WEEK_CELL_MAX_HEIGHT_PX
                            : undefined,
                        }}
                      >
                        <Stack spacing={isMonth ? 0.35 : 0.75}>
                          {visibleRows.map((b) => renderBookingCard(b, cardViewMode))}
                          {overflowCount > 0 && (
                            <Button
                              size="small"
                              variant="text"
                              onClick={() => openCalendarDayView(day.key)}
                              sx={{
                                minHeight: 22,
                                py: 0,
                                px: 0.5,
                                fontSize: "0.68rem",
                                justifyContent: "flex-start",
                                textTransform: "none",
                              }}
                            >
                              +{overflowCount} more
                            </Button>
                          )}
                          {!rows.length && (
                            <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.7rem" }}>
                              No bookings
                            </Typography>
                          )}
                        </Stack>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
          <Stack direction={{ xs: "column", lg: "row" }} spacing={1.25} sx={{ mb: 2 }}>
            <TextField
              label="Search booking/contact"
              size="small"
              value={bookingListFilters.search}
              onChange={(e) => setBookingListFilters({ ...bookingListFilters, search: e.target.value })}
              sx={{ minWidth: { xs: "100%", lg: 240 } }}
            />
            <TextField
              select
              label="Status"
              size="small"
              value={bookingListFilters.status}
              onChange={(e) => setBookingListFilters({ ...bookingListFilters, status: e.target.value })}
              sx={{ minWidth: { xs: "100%", lg: 170 } }}
            >
              <MenuItem value="">All statuses</MenuItem>
              {Array.from(new Set(bookings.map((booking) => booking.status).filter(Boolean))).map((status) => (
                <MenuItem key={status} value={status}>{status}</MenuItem>
              ))}
            </TextField>
            <TextField
              select
              label="Service"
              size="small"
              value={bookingListFilters.type_id}
              onChange={(e) => setBookingListFilters({ ...bookingListFilters, type_id: e.target.value })}
              sx={{ minWidth: { xs: "100%", lg: 210 } }}
            >
              <MenuItem value="">All services</MenuItem>
              {types.map((type) => (
                <MenuItem key={type.id} value={String(type.id)}>{type.name}</MenuItem>
              ))}
            </TextField>
            <TextField
              select
              label="Resource"
              size="small"
              value={bookingListFilters.resource_id}
              onChange={(e) => setBookingListFilters({ ...bookingListFilters, resource_id: e.target.value })}
              sx={{ minWidth: { xs: "100%", lg: 210 } }}
            >
              <MenuItem value="">All resources</MenuItem>
              {resources.map((resource) => (
                <MenuItem key={resource.id} value={String(resource.id)}>{resource.name}</MenuItem>
              ))}
            </TextField>
            <TextField
              type="date"
              label="From"
              size="small"
              value={bookingListFilters.date_from}
              onChange={(e) => setBookingListFilters({ ...bookingListFilters, date_from: e.target.value })}
              InputLabelProps={{ shrink: true }}
              sx={{ minWidth: { xs: "100%", lg: 160 } }}
            />
            <TextField
              type="date"
              label="To"
              size="small"
              value={bookingListFilters.date_to}
              onChange={(e) => setBookingListFilters({ ...bookingListFilters, date_to: e.target.value })}
              InputLabelProps={{ shrink: true }}
              sx={{ minWidth: { xs: "100%", lg: 160 } }}
            />
            <Button
              variant="text"
              onClick={() => setBookingListFilters({ search: "", status: "", type_id: "", resource_id: "", date_from: "", date_to: "" })}
              sx={{ alignSelf: { lg: "center" } }}
            >
              Clear
            </Button>
          </Stack>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            Showing {visibleBookingRows.length} of {filteredBookingRows.length} filtered records ({bookings.length} total)
          </Typography>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                sm: "repeat(auto-fill, minmax(340px, 420px))",
              },
              justifyContent: "start",
              alignItems: "stretch",
              gap: 1.5,
              maxHeight: 620,
              overflowY: "auto",
              pr: 0.5,
            }}
          >
            {visibleBookingRows.map((b) => {
              const labels = getBookingLabel(b);
              const cancelled = String(b.status || "").startsWith("cancelled");
              const manageable = canManageBooking(b);
              return (
                <Box
                  key={b.id}
                  sx={{
                    p: 1.75,
                    borderRadius: 2.5,
                    border: "1px solid rgba(15,23,42,0.10)",
                    bgcolor: cancelled ? "rgba(15,23,42,0.03)" : "#fff",
                    minHeight: 162,
                    transition: "0.16s ease",
                    "&:hover": {
                      borderColor: "rgba(25,118,210,0.35)",
                      boxShadow: "0 10px 24px rgba(15,23,42,0.08)",
                    },
                  }}
                >
                  <Stack justifyContent="space-between" gap={1.5} sx={{ height: "100%" }}>
                    <Box sx={{ minWidth: 0 }}>
                      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.75 }}>
                        <Chip size="small" label={b.status || "pending"} color={cancelled ? "default" : "primary"} />
                        <Chip size="small" variant="outlined" label={`#${b.id}`} />
                      </Stack>
                      <Typography sx={{ fontWeight: 800, color: "#111827" }}>{labels.typeName}</Typography>
                      <Typography variant="body2" color="text.secondary">{labels.resourceName}</Typography>
                      <Typography variant="caption" color="text.secondary" display="block">
                        {b.starts_at_utc ? new Date(b.starts_at_utc).toLocaleString() : "Start time not set"}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" display="block">
                        Contact: {formatBookingContact(b)}
                      </Typography>
                    </Box>
                    <Stack direction="row" spacing={0.75} alignItems="center" justifyContent="flex-end" flexWrap="wrap">
                      <Button
                        size="small"
                        variant="contained"
                        disabled={!manageable}
                        onClick={() => setRescheduleForm({
                          id: b.id,
                          starts_at_utc: b.starts_at_utc
                            ? new Date(b.starts_at_utc).toISOString().slice(0, 16)
                            : "",
                          resource_id: b.resource_id ? String(b.resource_id) : "",
                        })}
                      >
                        Reschedule
                      </Button>
                      {!cancelled && (
                        <Button size="small" color="error" disabled={!manageable} onClick={() => cancelBooking(b)}>
                          Cancel
                        </Button>
                      )}
                    </Stack>
                  </Stack>
                </Box>
              );
            })}
            {!visibleBookingRows.length && (
              <Box sx={{ p: 3, textAlign: "center", border: "1px dashed rgba(15,23,42,0.18)", borderRadius: 2.5 }}>
                <Typography sx={{ fontWeight: 700 }}>No matching bookings</Typography>
                <Typography variant="body2" color="text.secondary">
                  Adjust filters or wait for customer bookings from WhatsApp flows.
                </Typography>
              </Box>
            )}
          </Box>
          {bookingPageCount > 1 && (
            <Stack direction="row" justifyContent="flex-end" sx={{ mt: 2 }}>
              <Pagination
                count={bookingPageCount}
                page={currentBookingPage}
                onChange={(_, page) => setBookingPage(page)}
                color="primary"
              />
            </Stack>
          )}
          {rescheduleForm.id && (
            <Box sx={{ mt: 2, p: 2, border: "1px solid", borderColor: "divider", borderRadius: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Reschedule booking #{rescheduleForm.id}
              </Typography>
              <Stack direction={{ xs: "column", md: "row" }} spacing={1}>
                <TextField
                  type="datetime-local"
                  label="New start"
                  value={rescheduleForm.starts_at_utc}
                  onChange={(e) => setRescheduleForm({ ...rescheduleForm, starts_at_utc: e.target.value })}
                  InputLabelProps={{ shrink: true }}
                />
                <TextField
                  select
                  label="Resource"
                  value={rescheduleForm.resource_id}
                  onChange={(e) => setRescheduleForm({ ...rescheduleForm, resource_id: e.target.value })}
                  sx={{ minWidth: 220 }}
                >
                  <MenuItem value="">Keep / no resource</MenuItem>
                  {resources.map((r) => (
                    <MenuItem key={r.id} value={String(r.id)}>{r.name}</MenuItem>
                  ))}
                </TextField>
                <Button variant="contained" onClick={saveReschedule}>Save reschedule</Button>
                <Button onClick={() => setRescheduleForm({ id: null, starts_at_utc: "", resource_id: "" })}>Cancel</Button>
              </Stack>
            </Box>
          )}
        </SectionCard>
      )}

      {tab === "chatbot" && (
        <Stack spacing={2}>
          <Alert severity="info">
            Chatbot flows should fetch appointment services/resources from these APIs and pass IDs to action nodes.
            Do not duplicate appointment inventory inside the flow JSON.
          </Alert>
          <SectionCard
            title="Recommended WhatsApp booking flow"
            subtitle="Use Question nodes for customer input and Vertical Action nodes for availability, hold, and payment."
          >
            <Stack spacing={1.25} component="ol" sx={{ pl: 2.5, m: 0 }}>
              <Typography component="li" variant="body2">
                <b>Question</b> — “Which date do you prefer?” → save as <code>preferred_date</code> (e.g. 2026-05-20 or today).
              </Typography>
              <Typography component="li" variant="body2">
                <b>Vertical action: Appointment — Check slots</b> — pick service + resource; uses <code>preferred_date</code> for a 7-day window.
              </Typography>
              <Typography component="li" variant="body2">
                <b>Message</b>
                {" — “Available times:"}
                <br />
                <code>{"{{appointment_slots_text}}"}</code>
                <br />
                {"Reply with the number (1, 2, 3…).”"}
              </Typography>
              <Typography component="li" variant="body2">
                <b>Question</b> — “Reply with your slot number” → save as <code>selected_slot</code>.
              </Typography>
              <Typography component="li" variant="body2">
                <b>Vertical action: Appointment — Reserve slot</b> — slot/time variable = <code>selected_slot</code> (accepts 1, 2, 3 from the list).
              </Typography>
              <Typography component="li" variant="body2">
                <b>Book without payment:</b> <b>Vertical action: Appointment — Confirm booking (no payment)</b> → confirmation message with {"{{appointmentbooking}}"} (creates the booking row shown in Bookings).
              </Typography>
              <Typography component="li" variant="body2">
                <b>Book with WhatsApp pay:</b> <b>Vertical action: Appointment — Prepare payment</b> → <b>Payment</b> node → confirmation message (payment webhook confirms the booking).
              </Typography>
            </Stack>
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1.5 }}>
              Configure staff calendars under Resources before going live. A question node alone cannot read the calendar — the Check slots action does.
            </Typography>
          </SectionCard>
          <SectionCard title="Chatbot action payload builder" subtitle="Pick merchant-configured data and use the generated payload in action nodes.">
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <TextField
                  select
                  fullWidth
                  label="Appointment service"
                  value={chatbotPreview.appointment_type_id}
                  onChange={(e) => setChatbotPreview({ ...chatbotPreview, appointment_type_id: e.target.value })}
                >
                  {types.filter((t) => t.is_active !== false && t.is_active !== 0).map((t) => (
                    <MenuItem key={t.id} value={String(t.id)}>{t.name} ({t.code})</MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  select
                  fullWidth
                  label="Resource"
                  value={chatbotPreview.resource_id}
                  onChange={(e) => setChatbotPreview({ ...chatbotPreview, resource_id: e.target.value })}
                >
                  <MenuItem value="">Any mapped resource</MenuItem>
                  {resources
                    .filter((r) => r.is_active !== false && r.is_active !== 0)
                    .filter((r) => {
                      const assigned = asArray(r.appointment_type_ids);
                      return !chatbotPreview.appointment_type_id
                        || assigned.length === 0
                        || assigned.some((id) => String(id) === String(chatbotPreview.appointment_type_id));
                    })
                    .map((r) => (
                      <MenuItem key={r.id} value={String(r.id)}>{r.name}</MenuItem>
                    ))}
                </TextField>
              </Grid>
              <Grid item xs={12} md={4}>
                <Stack spacing={0.5}>
                  <Typography variant="body2" color="text.secondary">Selected</Typography>
                  <Typography>{selectedTypeForPreview?.name || "No appointment service selected"}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {selectedResourceForPreview?.name || "Resource selected by flow/customer"}
                  </Typography>
                </Stack>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  minRows={7}
                  label="availability action payload"
                  value={JSON.stringify(availabilityPayloadPreview, null, 2)}
                  InputProps={{ readOnly: true }}
                />
              </Grid>
            </Grid>
          </SectionCard>
          <SectionCard title="Dynamic data sources">
            <Stack spacing={1}>
              <Typography><b>Services:</b> GET /w1/vertical/{serviceId}/appointment-types</Typography>
              <Typography><b>Resources:</b> GET /w1/vertical/{serviceId}/appointment-resources</Typography>
              <Typography><b>Availability:</b> POST /w1/vertical/{serviceId}/appointment/actions/availability</Typography>
              <Typography><b>Hold:</b> POST /w1/vertical/{serviceId}/appointment/actions/hold</Typography>
              <Typography><b>Payment:</b> attach-payment + send-native-review-pay</Typography>
            </Stack>
          </SectionCard>
          <SectionCard title="Registered action nodes" subtitle="Fetched from /vertical/actions-registry.">
            {registryActions.map((a) => (
              <Box key={a.id} sx={{ py: 1 }}>
                <Stack direction={{ xs: "column", md: "row" }} spacing={1} alignItems={{ md: "center" }}>
                  <Chip label={a.method} size="small" />
                  <Typography fontWeight={700}>{a.id}</Typography>
                  <Typography color="text.secondary">{a.path}</Typography>
                </Stack>
                {a.body && <Typography variant="caption" color="text.secondary">Body: {a.body.join(", ")}</Typography>}
                <Divider sx={{ mt: 1 }} />
              </Box>
            ))}
          </SectionCard>
          <SectionCard title="Recommended appointment error branches" subtitle="Use these as if-condition branches after availability, hold, payment, cancel, and reschedule action nodes.">
            <Stack spacing={1}>
              {asArray(registry?.recommended_chatbot_nodes?.appointment_booking).map((node) => (
                <Box key={node.id}>
                  <Typography fontWeight={700}>{node.id}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {node.type}{node.action_id ? ` • ${node.action_id}` : ""}{node.purpose ? ` • ${node.purpose}` : ""}
                  </Typography>
                  {asArray(node.conditions).map((condition) => (
                    <Typography key={`${node.id}-${condition.error_code}`} variant="caption" display="block" color="text.secondary">
                      {condition.error_code} → {condition.next}
                    </Typography>
                  ))}
                  <Divider sx={{ mt: 1 }} />
                </Box>
              ))}
              {Object.entries(registry?.error_codes?.appointment || {}).map(([code, message]) => (
                <Typography key={code} variant="caption" display="block">
                  <b>{code}</b>: {message}
                </Typography>
              ))}
            </Stack>
          </SectionCard>
        </Stack>
      )}

      {noticeNode}
    </Box>
  );
}

export default AppointmentOperationsView;
